import Markdown from 'react-markdown';
import { CodeBlock, theme } from '@agentisai/design-system';
import type { GetArtifactData, TurnArtifact } from '@agentisai/shared-types';

/**
 * Map a fetched artifact onto the shape {@link ArtifactPreview} renders, or
 * `null` when it has nothing this component can show (the caller then renders
 * its download-only note).
 *
 * ONE definition of "previewable", used by every panel: the two that existed
 * drifted — the Files rail learned PDFs (Drop-7 S02) and the conversation's
 * deliverables panel did not, so the same PDF previewed in one place and read
 * "no inline preview for this file type" in the other.
 *
 * `url` is `previewUrl` — the inline-disposed presign — NOT `data.url`, which
 * is attachment-disposed for the Download link and forces a download inside an
 * iframe too.
 */
export function previewableArtifact(data: GetArtifactData): TurnArtifact | null {
  // Text-family needs `content`; a PDF renders from `previewUrl` in a native
  // iframe. Anything else has no inline form.
  const canPreview =
    data.content != null || (data.previewUrl != null && data.contentType.includes('pdf'));
  if (!canPreview) return null;
  return {
    filename: data.filename,
    contentType: data.contentType,
    sizeBytes: data.sizeBytes,
    key: data.artifactId,
    url: data.previewUrl,
    preview: data.content,
  };
}

/** CodeBlock language stamp for a preview's content type. */
function previewLanguage(contentType: string): string | undefined {
  if (contentType.includes('json')) return 'json';
  return undefined;
}

/**
 * ArtifactPreview — renders an artifact's inline preview by content type
 * (ADR-0040 / Stage 3c):
 *   - HTML → `iframe srcdoc` with `sandbox="allow-scripts"` so agent-built
 *     interactive HTML runs instead of rendering inert. `allow-same-origin`
 *     is deliberately withheld — the frame keeps an opaque origin (no
 *     cookies/storage/parent-DOM reach) and inherits the SPA's CSP; that
 *     pairing is the security posture for untrusted agent output;
 *   - markdown → rich render via react-markdown (safe by default — raw HTML
 *     in the source is NOT rendered; the Stage 3b PRD → spec document flow
 *     is the "real report" ADR-0040 deferred this for);
 *   - anything else text-family → DS CodeBlock.
 * Content only — callers own any disclosure/card chrome around it.
 */
export function ArtifactPreview({ artifact }: { artifact: TurnArtifact }) {
  // PDFs are binary — no text content to inline. Render the presigned GET URL
  // in a native browser PDF viewer. No sandbox: sandboxed iframes block PDF
  // navigation in all major browsers.
  if (artifact.contentType.includes('pdf') && artifact.url) {
    return (
      <iframe
        src={artifact.url}
        title={`Preview of ${artifact.filename}`}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    );
  }

  if (!artifact.preview) return null;

  if (artifact.contentType.includes('html')) {
    // Fill the ArtifactWorkspace body (a `flex: 1` panel) so the preview is a
    // full-height viewport of the agent's page — like a browser tab — instead
    // of a fixed 280px slice leaving the column half-empty. `display: block`
    // drops the inline-replaced descender gap that would otherwise force a
    // scrollbar; no border/radius since the panel (tab + meta strips) already
    // frames it, and a full-bleed viewport reads as the document surface. The
    // agent's page scrolls inside the frame when it's taller than the panel.
    return (
      <iframe
        sandbox="allow-scripts"
        srcDoc={artifact.preview}
        title={`Preview of ${artifact.filename}`}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 0,
          background: theme.color.neutral.bgSurface,
        }}
      />
    );
  }

  if (artifact.contentType.includes('markdown')) {
    return (
      <div
        style={{
          padding: `0 ${theme.space[2]}`,
          fontSize: theme.typography.fontSize.bodySm,
          lineHeight: theme.typography.lineHeight.docBody,
          color: theme.color.neutral.body,
          overflowWrap: 'break-word',
        }}
      >
        <Markdown>{artifact.preview}</Markdown>
      </div>
    );
  }

  return <CodeBlock code={artifact.preview} language={previewLanguage(artifact.contentType)} />;
}
ArtifactPreview.displayName = 'ArtifactPreview';
